import React from 'react';
import { StyleSheet } from 'react-native';

import { TeamInfoWithPlayersFragment } from '@/api/events/query.generated';
import { Logo } from '@/components/MergedLogos';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { leagueConfigSelector, useLeagueConfigsStore } from '@/feature/betslip-pickem/hooks/use-league-configs-store';
import { ColorMatrixImage } from '@/feature/entries-pickem/components/ColorMatrixImage';
import { League } from '@/types/api.generated';
import { isCollegeSport, isUFC } from '@/utils/league';
import { useShallow } from 'zustand/react/shallow';

const styles = StyleSheet.create({
    logoStyle: {
        alignSelf: 'center',
        width: 16,
        height: 16,
    },
    logoOnlyStyle: {
        alignSelf: 'center',
        width: 20,
        height: 20,
    },
    canvas: {
        flex: 1,
    },
});

export const LeagueLogo = ({
    league,
    mode,
    team,
}: {
    league: League;
    mode?: 'logoOnly';
    team?: TeamInfoWithPlayersFragment;
}) => {
    const { leagueIcon, leagueColor } = useLeagueConfigsStore(useShallow(leagueConfigSelector(league)));
    const icon = mode === 'logoOnly' && team ? team.icon : leagueIcon ?? '';
    const teamColor = team?.color;
    const secondaryColor = team?.secondaryColor;
    // Display the UFC red logo for UFC leagues on mobile using Skia - which is not available on web
    const displayUFCRedLogo = isUFC(league);

    switch (mode) {
        case 'logoOnly':
            return (
                <>
                    {displayUFCRedLogo ? (
                        <Box paddingVertical={'s4'} width={20} flex={1}>
                            <ColorMatrixImage icon={icon} />
                        </Box>
                    ) : (
                        <Box paddingVertical={'s4'}>
                            <Logo
                                icon={icon}
                                logoStyle={styles.logoOnlyStyle}
                                logoSize={20}
                                shouldShowFallbackLogo={isCollegeSport(league)}
                                teamFallbackTextColor={ds.colorValue.text.primary}
                                primaryColor={teamColor}
                                secondaryColor={secondaryColor ?? ds.colorValue.icon.primary}
                                renderMode={isCollegeSport(league) ? 'shield' : 'team'}
                            />
                        </Box>
                    )}
                </>
            );
        default:
            return (
                <Box
                    style={{ backgroundColor: leagueColor || ds.colorValue.background.neutral.inverse }}
                    padding={'s4'}
                    borderRadius={'r8'}
                >
                    <Logo
                        icon={leagueIcon || ''}
                        logoStyle={styles.logoStyle}
                        logoSize={24}
                        teamFallbackTextColor={ds.colorValue.text.primary}
                        secondaryColor={ds.colorValue.icon.primary}
                        renderMode={'team'}
                    />
                </Box>
            );
    }
};
